import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/api";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";

const Companies = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await fetch(`${API_BASE_URL}/company/getAll`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch companies");
        }

        const data = await response.json();
        if (data.subCode === 200 && data.items) {
          setCompanies(data.items);
        } else {
          setError("Failed to load companies: " + (data.message || "Unknown error"));
        }
      } catch (err) {
        setError(err.message);
        console.error("Companies fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // DataGrid columns
  const columns = [
    { field: "_id", headerName: "Company ID", flex: 0.8 },
    { 
      field: "name", 
      headerName: "Company Name", 
      flex: 1.5,
      cellClassName: "name-column--cell" 
    },
    { field: "airport_name", headerName: "Airport", flex: 1 },
    { field: "registered_owner", headerName: "Owner", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.2 },
    { field: "contact_number", headerName: "Contact", flex: 1 },
    { 
      field: "createdAt", 
      headerName: "Created", 
      flex: 1,
      valueGetter: (params) => formatDate(params.value)
    },
  ];

  // Transform data for DataGrid
  const gridData = companies.map((company, index) => ({
    id: company._id || index,
    ...company,
  }));

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && companies.length === 0) {
    return (
      <Box m="20px">
        <Header title="ALL COMPANIES" subtitle="View all registered companies" />
        <Box color="error.main" p={2}>
          Error: {error}
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="ALL COMPANIES" subtitle="View and manage all registered companies" />

      {/* Companies Count */}
      <Typography variant="h6" mb={2} color={colors.grey[100]}>
        Showing {companies.length} compan{companies.length !== 1 ? "ies" : "y"}
      </Typography>

      {/* View Toggle - Cards View */}
      <Grid container spacing={3}>
        {companies.map((company, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={company._id || index}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: colors.primary[400],
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              {/* Company Image */}
              {company.image && (
                <CardMedia
                  component="img"
                  height="180"
                  image={
                    company.image.startsWith("http")
                      ? company.image
                      : `https://app.airportparkwithus.co.uk/images/upload/${company.image}`
                  }
                  alt={company.name}
                  sx={{
                    objectFit: "contain",
                    backgroundColor: colors.primary[500],
                    p: 2,
                  }}
                  onError={(e) => {
                    e.target.src = "/assets/user.png";
                  }}
                />
              )}

              <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                {/* Company Name */}
                <Box display="flex" alignItems="center" mb={1}>
                  <BusinessIcon sx={{ color: colors.blueAccent[500], fontSize: "20px", mr: 1 }} />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={colors.grey[100]}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {company.name}
                  </Typography>
                </Box>

                {/* Company ID */}
                <Chip
                  label={`ID: ${company._id}`}
                  size="small"
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: "0.7rem",
                    mb: 1.5,
                    width: "fit-content",
                  }}
                />

                <Divider sx={{ mb: 1.5, backgroundColor: colors.primary[500] }} />

                {/* Airport */}
                {company.airport_name && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <LocationOnIcon
                      sx={{ color: colors.greenAccent[500], fontSize: "16px", mr: 1 }}
                    />
                    <Typography variant="body2" color={colors.grey[300]}>
                      {company.airport_name}
                    </Typography>
                  </Box>
                )}

                {/* Registered Owner */}
                {company.registered_owner && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <PersonIcon
                      sx={{ color: colors.greenAccent[500], fontSize: "16px", mr: 1 }}
                    />
                    <Typography variant="body2" color={colors.grey[300]}>
                      {company.registered_owner}
                    </Typography>
                  </Box>
                )}

                {/* Contact Information */}
                <Box mt="auto" pt={1}>
                  {company.contact_number && (
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <PhoneIcon
                        sx={{ color: colors.blueAccent[500], fontSize: "14px", mr: 0.5 }}
                      />
                      <Typography
                        variant="caption"
                        color={colors.grey[400]}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {company.contact_number}
                      </Typography>
                    </Box>
                  )}
                  {company.email && (
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <EmailIcon
                        sx={{ color: colors.blueAccent[500], fontSize: "14px", mr: 0.5 }}
                      />
                      <Typography
                        variant="caption"
                        color={colors.grey[400]}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {company.email}
                      </Typography>
                    </Box>
                  )}
                  {company.customer_support_email && (
                    <Box display="flex" alignItems="center">
                      <EmailIcon
                        sx={{ color: colors.blueAccent[500], fontSize: "14px", mr: 0.5 }}
                      />
                      <Typography
                        variant="caption"
                        color={colors.grey[400]}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Support: {company.customer_support_email}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Additional Info */}
                <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" gap={0.5}>
                  {company.registration_no && (
                    <Chip
                      label={`Reg: ${company.registration_no}`}
                      size="small"
                      sx={{
                        backgroundColor: colors.greenAccent[700],
                        color: colors.grey[100],
                        fontSize: "0.65rem",
                      }}
                    />
                  )}
                  {company.address && (
                    <Chip
                      icon={<HomeIcon sx={{ fontSize: "12px !important" }} />}
                      label="Address"
                      size="small"
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        fontSize: "0.65rem",
                      }}
                      title={company.address}
                    />
                  )}
                  {company.documents && (
                    <Chip
                      icon={<DescriptionIcon sx={{ fontSize: "12px !important" }} />}
                      label="Docs"
                      size="small"
                      sx={{
                        backgroundColor: colors.primary[600],
                        color: colors.grey[100],
                        fontSize: "0.65rem",
                      }}
                    />
                  )}
                </Stack>

                {/* Dates */}
                {(company.createdAt || company.updatedAt) && (
                  <Box mt={1.5} pt={1} borderTop={`1px solid ${colors.primary[500]}`}>
                    {company.createdAt && (
                      <Typography variant="caption" color={colors.grey[500]}>
                        Created: {formatDate(company.createdAt)}
                      </Typography>
                    )}
                    {company.updatedAt && company.createdAt !== company.updatedAt && (
                      <Typography
                        variant="caption"
                        color={colors.grey[500]}
                        display="block"
                        mt={0.5}
                      >
                        Updated: {formatDate(company.updatedAt)}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Table View */}
      <Box mt={4}>
        <Typography variant="h6" mb={2} color={colors.grey[100]}>
          Table View
        </Typography>
        <Box
          m="40px 0 0 0"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .name-column--cell": {
              color: colors.greenAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            rows={gridData}
            columns={columns}
            loading={loading}
            components={{ Toolbar: GridToolbar }}
          />
        </Box>
      </Box>

      {companies.length === 0 && !loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="40vh"
          flexDirection="column"
        >
          <Typography variant="h5" color={colors.grey[300]} mb={2}>
            No companies found
          </Typography>
          <Typography variant="body2" color={colors.grey[400]}>
            Add a new company to get started
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Companies;


